import { c as _c } from "react/compiler-runtime";
import { IcoPlusSmall16 } from '@onefootprint/icons';
import { Button, Card, Divider, NotionEditor, useConfirmationDialog } from '@onefootprint/ui';
import type { Editor } from '@tiptap/core';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createInlineMarkupRegistry, EditableText } from '@/components/block-primitives';
import DeleteReportButton from '../../section-editor/report/delete-report-button';
import ReportDocument from '../../section-editor/report/report-document';
import { outlineOf } from '../../section-editor/report/report-outline';
import { type FieldKeyError, namedFieldKeyError } from '../../store/field-key';
import type { ArtifactType, FieldType, Property } from '../../store/types';
import { isEnumType, isObjectType } from '../../store/types';
import { artifactRowId, fieldRowId, padEnumValues } from '../../store/utils';
import type { SchemaEditorState, SchemaPatch } from '../../store/wire';
import { agentBlockSlashCommands } from '../agent-blocks/agent-block-slash-commands';
import { TaskBlockProvider } from '../agent-blocks/task-block-context';
import FieldKeyErrorMessage from '../field-key-error';
import MoveControls from '../move-controls';
import usePromptMentionExtensions from '../prompt-mention/use-prompt-mention-extensions';
import { FIELD_TYPE_BADGE, SchemaTypeSelect } from '../task/schema';
import { EnumCriteriaTable, OutputColumns, OutputDetail, PropertyColumns, PropertyRows } from './output-detail';
import { RemoveButton, SectionHeading, TypeChip } from './primitives';
import type { ArtifactState, FieldState, TaskState } from './state';
import { newField } from './state';
import TaskSkills from './task-skills';
const outputMarkup = createInlineMarkupRegistry([]);

// 24 / 20 / 16px under the pane's 28px title. Arbitrary values with `!`, not DS tokens: the editor's
// own rules live in `@layer notion-editor`, which the cascade places after Tailwind's utilities, so an
// override without `!` loses however specific it is — and the DS type tokens are hand-written CSS that
// no `[&_...]` variant can wrap.
const EDITOR_TYPOGRAPHY = `
  [&_.notion-editor]:text-body-3
  [&_.notion-editor_h1]:text-[1.5rem]!
  [&_.notion-editor_h2]:text-[1.25rem]!
  [&_.notion-editor_h3]:text-[1rem]!
  [&_.notion-editor_p]:my-0! [&_.notion-editor_p]:py-0!
  [&_.notion-editor_p:not(:first-child)]:mt-2!
`;
type SaveOptions = {
  save?: boolean;
};
const SAVE: SaveOptions = {
  save: true
};

// Everything a property edit can change but its text. Type and enum pickers commit from a portal,
// after the blur save has run, so these edits must save on their own.
const propertyShape = (properties: Property[] | null | undefined): string => JSON.stringify((properties ?? []).map(property => [property.id, property.type, property.enumValues?.length ?? 0, propertyShape(property.children)]));
type PromptEditorProps = {
  className?: string;
  content: string;
  editable: boolean;
  label: string;
  /** The task hosting this prompt; the @ menu only offers what runs before it. */
  nodeId: string;
  placeholder: string;
  promptKey?: string;
  onChange: (markdown: string) => void;
  onEditorReady?: (editor: Editor) => void;
};
export const PromptEditor = t0 => {
  const $ = _c(20);
  const {
    className,
    content,
    editable,
    label,
    nodeId,
    promptKey,
    placeholder,
    onChange,
    onEditorReady
  } = t0;
  const extensions = usePromptMentionExtensions(nodeId);
  const t1 = promptKey === undefined ? "task" : "field";
  let t2;
  if ($[0] !== nodeId || $[1] !== promptKey || $[2] !== t1) {
    t2 = {
      nodeId,
      promptKind: t1,
      promptKey
    };
    $[0] = nodeId;
    $[1] = promptKey;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== className) {
    t3 = cx(EDITOR_TYPOGRAPHY, className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== content || $[7] !== editable || $[8] !== extensions || $[9] !== onChange || $[10] !== onEditorReady || $[11] !== placeholder) {
    t4 = <NotionEditor content={content} editable={editable} emitOnMount={false} extensions={extensions} placeholder={placeholder} slashCommands={agentBlockSlashCommands} onChange={onChange} onEditorReady={onEditorReady} />;
    $[6] = content;
    $[7] = editable;
    $[8] = extensions;
    $[9] = onChange;
    $[10] = onEditorReady;
    $[11] = placeholder;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== label || $[14] !== t3 || $[15] !== t4) {
    t5 = <div role="group" aria-label={label} className={t3}>{t4}</div>;
    $[13] = label;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t2 || $[18] !== t5) {
    t6 = <TaskBlockProvider value={t2}>{t5}</TaskBlockProvider>;
    $[17] = t2;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};
type FieldRowProps = {
  nodeId: string;
  field: FieldState;
  canDown: boolean;
  canUp: boolean;
  editable: boolean;
  isHighlighted: boolean;
  /** Percy's pending edits to this output, under the row they would change. */
  children?: ReactNode;
  /** A key later steps cannot read: an invalid or duplicated one stops the task from saving at all. */
  keyError?: FieldKeyError;
  onField: (next: FieldState, options?: SaveOptions) => void;
  onMove: (direction: 'up' | 'down') => void;
  onRemove: () => void;
};
const FieldRow = t0 => {
  const $ = _c(125);
  const {
    nodeId,
    field,
    canDown,
    canUp,
    editable,
    isHighlighted,
    children,
    keyError,
    onField,
    onMove,
    onRemove
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task.output"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const confirm = useConfirmationDialog();
  let T0;
  let applyPatch;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let type;
  if ($[1] !== canDown || $[2] !== canUp || $[3] !== confirm || $[4] !== editable || $[5] !== field || $[6] !== isHighlighted || $[7] !== keyError || $[8] !== onField || $[9] !== onMove || $[10] !== onRemove || $[11] !== t) {
    const named = field.key || t(_temp);
    type = (field.typeLabel || "text") as FieldType;
    const t15 = field.enumValues ?? undefined;
    const t16 = field.criteria ?? undefined;
    const t17 = field.children ?? undefined;
    let t18;
    if ($[28] !== t15 || $[29] !== t16 || $[30] !== t17 || $[31] !== type) {
      t18 = {
        type,
        enumValues: t15,
        criteria: t16,
        children: t17
      };
      $[28] = t15;
      $[29] = t16;
      $[30] = t17;
      $[31] = type;
      $[32] = t18;
    } else {
      t18 = $[32];
    }
    const schema = t18;
    let t19;
    if ($[33] !== field || $[34] !== onField || $[35] !== type) {
      t19 = patch => {
        const nextType = (patch.type ?? type) as FieldType;
        const next = {
          ...field,
          typeLabel: nextType,
          enumValues: patch.enumValues ?? field.enumValues,
          children: patch.children ?? field.children,
          criteria: patch.criteria ?? field.criteria
        };
        const structural = nextType !== type || (next.enumValues ?? []).length !== (field.enumValues ?? []).length || propertyShape(next.children) !== propertyShape(field.children);
        onField(next, structural ? SAVE : undefined);
      };
      $[33] = field;
      $[34] = onField;
      $[35] = type;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    applyPatch = t19;
    const handleDelete = () => confirm.open({
      title: t(_temp2),
      description: t(_temp3, {
        name: named
      }),
      variant: "destructive",
      primaryButton: {
        label: t(_temp4),
        onClick: onRemove
      },
      secondaryButton: {
        label: t(_temp5)
      }
    });
    if ($[37] !== field.key || $[38] !== t) {
      t10 = field.key || t(_temp6);
      $[37] = field.key;
      $[38] = t;
      $[39] = t10;
    } else {
      t10 = $[39];
    }
    const t20 = isHighlighted ? "bg-active" : "bg-primary";
    if ($[40] !== t20) {
      t11 = cx("group/field flex flex-col gap-4 rounded-default border border-tertiary px-2 py-3 transition-colors duration-300", t20);
      $[40] = t20;
      $[41] = t11;
    } else {
      t11 = $[41];
    }
    if ($[42] !== field.uid) {
      t12 = fieldRowId(field.uid);
      $[42] = field.uid;
      $[43] = t12;
    } else {
      t12 = $[43];
    }
    if ($[44] !== field.key || $[45] !== t) {
      t13 = field.key || t(_temp7);
      $[44] = field.key;
      $[45] = t;
      $[46] = t13;
    } else {
      t13 = $[46];
    }
    t14 = field.markdown;
    t7 = "flex flex-col gap-3";
    let t21;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <span className="shrink-0 text-label-2 text-tertiary">outputs.</span>;
      $[47] = t21;
    } else {
      t21 = $[47];
    }
    const t22 = `min-w-0 px-1 py-0.5 font-medium! text-snippet-1 opacity-100! ${keyError ? "text-error" : "text-primary"}`;
    let t23;
    if ($[48] !== t) {
      t23 = t(_temp8);
      $[48] = t;
      $[49] = t23;
    } else {
      t23 = $[49];
    }
    let t24;
    if ($[50] !== field || $[51] !== onField) {
      t24 = next_0 => onField({
        ...field,
        key: next_0.trim()
      });
      $[50] = field;
      $[51] = onField;
      $[52] = t24;
    } else {
      t24 = $[52];
    }
    let t25;
    if ($[53] !== t) {
      t25 = t(_temp9);
      $[53] = t;
      $[54] = t25;
    } else {
      t25 = $[54];
    }
    let t26;
    if ($[55] !== editable || $[56] !== field.key || $[57] !== t22 || $[58] !== t23 || $[59] !== t24 || $[60] !== t25) {
      t26 = <span className="flex min-w-0 items-baseline gap-0.5">{t21}<span className="min-w-0 rounded-sm transition-colors hover:bg-hover in-data-force-affordance:bg-hover"><EditableText as="div" className={t22} editable={editable} label={t23} onSubmit={t24} placeholder={t25} registry={outputMarkup} value={field.key} /></span></span>;
      $[55] = editable;
      $[56] = field.key;
      $[57] = t22;
      $[58] = t23;
      $[59] = t24;
      $[60] = t25;
      $[61] = t26;
    } else {
      t26 = $[61];
    }
    let t27;
    if ($[62] !== applyPatch || $[63] !== editable || $[64] !== field.uid || $[65] !== schema || $[66] !== t || $[67] !== type) {
      t27 = editable ? <SchemaTypeSelect ariaLabel={t(_temp10)} canEdit={true} id={`output-${field.uid}-type`} onChange={applyPatch} schema={schema} /> : <TypeChip>{FIELD_TYPE_BADGE[type]}</TypeChip>;
      $[62] = applyPatch;
      $[63] = editable;
      $[64] = field.uid;
      $[65] = schema;
      $[66] = t;
      $[67] = type;
      $[68] = t27;
    } else {
      t27 = $[68];
    }
    let t28;
    if ($[69] !== canDown || $[70] !== canUp || $[71] !== editable || $[72] !== handleDelete || $[73] !== onMove || $[74] !== t) {
      t28 = editable && <span className="ml-auto flex shrink-0 items-center gap-0.5 transition-opacity md:opacity-0 md:group-hover/field:opacity-100 md:focus-within:opacity-100 md:in-data-force-affordance:opacity-100"><MoveControls canDown={canDown} canUp={canUp} onMove={onMove} visibility="always" /><RemoveButton label={t(_temp11)} onClick={handleDelete} /></span>;
      $[69] = canDown;
      $[70] = canUp;
      $[71] = editable;
      $[72] = handleDelete;
      $[73] = onMove;
      $[74] = t;
      $[75] = t28;
    } else {
      t28 = $[75];
    }
    if ($[76] !== t26 || $[77] !== t27 || $[78] !== t28) {
      t8 = <div className="flex items-center gap-2.5 px-2">{t26}{t27}{t28}</div>;
      $[76] = t26;
      $[77] = t27;
      $[78] = t28;
      $[79] = t8;
    } else {
      t8 = $[79];
    }
    if ($[80] !== keyError) {
      t9 = keyError && <div className="px-2"><FieldKeyErrorMessage error={keyError} /></div>;
      $[80] = keyError;
      $[81] = t9;
    } else {
      t9 = $[81];
    }
    T0 = PromptEditor;
    t2 = "px-2 py-1 [&_.notion-editor]:text-body-2!";
    t3 = field.key;
    t4 = field.markdown;
    t5 = editable;
    t6 = t(_temp12, {
      name: named
    });
    $[1] = canDown;
    $[2] = canUp;
    $[3] = confirm;
    $[4] = editable;
    $[5] = field;
    $[6] = isHighlighted;
    $[7] = keyError;
    $[8] = onField;
    $[9] = onMove;
    $[10] = onRemove;
    $[11] = t;
    $[12] = T0;
    $[13] = applyPatch;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = type;
  } else {
    T0 = $[12];
    applyPatch = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
    type = $[27];
  }
  let t15;
  if ($[82] !== field || $[83] !== onField) {
    t15 = markdown => onField({
      ...field,
      markdown
    });
    $[82] = field;
    $[83] = onField;
    $[84] = t15;
  } else {
    t15 = $[84];
  }
  let t16;
  if ($[85] !== t) {
    t16 = t(_temp13);
    $[85] = t;
    $[86] = t16;
  } else {
    t16 = $[86];
  }
  let t17;
  if ($[87] !== T0 || $[88] !== nodeId || $[89] !== t15 || $[90] !== t16 || $[91] !== t2 || $[92] !== t3 || $[93] !== t4 || $[94] !== t5 || $[95] !== t6) {
    t17 = <T0 className={t2} promptKey={t3} content={t4} editable={t5} label={t6} nodeId={nodeId} onChange={t15} placeholder={t16} />;
    $[87] = T0;
    $[88] = nodeId;
    $[89] = t15;
    $[90] = t16;
    $[91] = t2;
    $[92] = t3;
    $[93] = t4;
    $[94] = t5;
    $[95] = t6;
    $[96] = t17;
  } else {
    t17 = $[96];
  }
  let t18;
  if ($[97] !== t17 || $[98] !== t7 || $[99] !== t8 || $[100] !== t9) {
    t18 = <div className={t7}>{t8}{t9}{t17}</div>;
    $[97] = t17;
    $[98] = t7;
    $[99] = t8;
    $[100] = t9;
    $[101] = t18;
  } else {
    t18 = $[101];
  }
  let t19;
  if ($[102] !== applyPatch || $[103] !== editable || $[104] !== field.criteria || $[105] !== field.enumValues || $[106] !== t || $[107] !== type) {
    t19 = isEnumType(type) && (editable || (field.enumValues ?? []).length > 0) && <OutputDetail header={<OutputColumns left={t(_temp14)} right={t(_temp15)} />}><EnumCriteriaTable criteria={field.criteria ?? {}} editable={editable} onChange={applyPatch} values={editable ? padEnumValues(field.enumValues ?? []) : field.enumValues ?? []} /></OutputDetail>;
    $[102] = applyPatch;
    $[103] = editable;
    $[104] = field.criteria;
    $[105] = field.enumValues;
    $[106] = t;
    $[107] = type;
    $[108] = t19;
  } else {
    t19 = $[108];
  }
  let t20;
  if ($[109] !== applyPatch || $[110] !== editable || $[111] !== field.children || $[112] !== t || $[113] !== type) {
    t20 = isObjectType(type) && (editable || (field.children ?? []).length > 0) && <OutputDetail header={<PropertyColumns description={t(_temp16)} name={t(_temp17)} type={t(_temp18)} />}><PropertyRows editable={editable} onChange={children_0 => applyPatch({
        children: children_0
      })} properties={field.children ?? []} /></OutputDetail>;
    $[109] = applyPatch;
    $[110] = editable;
    $[111] = field.children;
    $[112] = t;
    $[113] = type;
    $[114] = t20;
  } else {
    t20 = $[114];
  }
  let t21;
  if ($[115] !== children || $[116] !== t10 || $[117] !== t11 || $[118] !== t12 || $[119] !== t13 || $[120] !== t14 || $[121] !== t18 || $[122] !== t19 || $[123] !== t20) {
    t21 = <section aria-label={t10} className={t11} data-id={t12} data-outline={t13} data-outline-preview={t14}>{t18}{t19}{t20}{children}</section>;
    $[115] = children;
    $[116] = t10;
    $[117] = t11;
    $[118] = t12;
    $[119] = t13;
    $[120] = t14;
    $[121] = t18;
    $[122] = t19;
    $[123] = t20;
    $[124] = t21;
  } else {
    t21 = $[124];
  }
  return t21;
};
type ReportCardProps = {
  artifact: ArtifactState;
  editable: boolean;
  isHighlighted: boolean;
  /** Percy's pending edits to this report, shown under it. */
  children?: ReactNode;
  onArtifact: (next: ArtifactState, options?: SaveOptions) => void;
  onRemove: () => void;
};
const ReportCard = t0 => {
  const $ = _c(27);
  const {
    artifact,
    editable,
    isHighlighted,
    children,
    onArtifact,
    onRemove
  } = t0;
  const type = (artifact.mimeType || "markdown") as ArtifactType;
  const t1 = isHighlighted ? "bg-active" : "bg-primary";
  let t2;
  if ($[0] !== t1) {
    t2 = cx("rounded-default border border-tertiary p-4 transition-colors duration-300", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== artifact.uid) {
    t3 = artifactRowId(artifact.uid);
    $[2] = artifact.uid;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== artifact.markdown || $[5] !== artifact.name || $[6] !== editable || $[7] !== onRemove || $[8] !== type) {
    t4 = editable && <DeleteReportButton name={artifact.name} isEmpty={type === "markdown" ? outlineOf(artifact.markdown).length === 0 : !artifact.markdown.trim()} onDelete={onRemove} />;
    $[4] = artifact.markdown;
    $[5] = artifact.name;
    $[6] = editable;
    $[7] = onRemove;
    $[8] = type;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] !== artifact || $[11] !== onArtifact) {
    t5 = name => onArtifact({
      ...artifact,
      name
    });
    t6 = (markdown, options) => onArtifact({
      ...artifact,
      markdown
    }, options);
    $[10] = artifact;
    $[11] = onArtifact;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[14] !== artifact.markdown || $[15] !== artifact.name || $[16] !== children || $[17] !== editable || $[18] !== t4 || $[19] !== t5 || $[20] !== t6 || $[21] !== type) {
    t7 = <ReportDocument name={artifact.name} spec={artifact.markdown} type={type} editable={editable} actions={t4} onName={t5} onSpec={t6}>{children}</ReportDocument>;
    $[14] = artifact.markdown;
    $[15] = artifact.name;
    $[16] = children;
    $[17] = editable;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = type;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== t2 || $[24] !== t3 || $[25] !== t7) {
    t8 = <div className={t2} data-id={t3}>{t7}</div>;
    $[23] = t2;
    $[24] = t3;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
};
type TaskEditorProps = {
  node: TaskState;
  readOnly?: boolean;
  takenOutputKeys: Set<string>;
  /** The `data-id` a jump is flashing, if it is one of this task's rows. */
  highlightedId?: string | null;
  /** Structural edits pass `save`: the pane saves text on blur, which an add or delete may never cause. */
  onNode: (update: (node: TaskState) => TaskState, options?: SaveOptions) => void;
  renderFieldSuggestions?: (fieldUid: string) => ReactNode;
  renderReportSuggestions?: (artifactUid: string) => ReactNode;
  onAddReport?: () => void;
  /** Runs before a report is removed. */
  onRemoveReport?: (artifactUid: string) => void;
  /** The prose editor only — not the per-output or per-report ones, which have their own titles. */
  onBodyEditorReady?: (editor: Editor) => void;
};
const TaskEditor = t0 => {
  const $ = _c(48);
  const {
    node,
    readOnly,
    takenOutputKeys,
    highlightedId,
    onNode,
    onBodyEditorReady,
    renderFieldSuggestions,
    renderReportSuggestions,
    onAddReport,
    onRemoveReport
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.task"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const editable = !readOnly;
  let t2;
  if ($[1] !== onNode) {
    t2 = (index, direction) => {
      onNode(current => {
        const fields = [...current.fields];
        const [moved] = fields.splice(index, 1);
        fields.splice(direction === "up" ? index - 1 : index + 1, 0, moved);
        return {
          ...current,
          fields
        };
      }, SAVE);
    };
    $[1] = onNode;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const move = t2;
  let t3;
  if ($[3] !== node.id) {
    t3 = {
      nodeId: node.id
    };
    $[3] = node.id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = node.markdown;
  let t5;
  if ($[5] !== t) {
    t5 = t(_temp19);
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = node.id;
  let t7;
  if ($[7] !== t) {
    t7 = t(_temp20);
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== onNode) {
    t8 = markdown => {
      onNode(current_0 => ({
        ...current_0,
        markdown
      }));
    };
    $[9] = onNode;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== editable || $[12] !== node.id || $[13] !== node.markdown || $[14] !== onBodyEditorReady || $[15] !== t5 || $[16] !== t7 || $[17] !== t8) {
    t9 = <PromptEditor content={t4} editable={editable} label={t5} nodeId={t6} placeholder={t7} onEditorReady={onBodyEditorReady} onChange={t8} />;
    $[11] = editable;
    $[12] = node.id;
    $[13] = node.markdown;
    $[14] = onBodyEditorReady;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== node.id) {
    t10 = <TaskSkills nodeId={node.id} />;
    $[19] = node.id;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== editable || $[22] !== highlightedId || $[23] !== move || $[24] !== node.fields || $[25] !== node.id || $[26] !== onNode || $[27] !== renderFieldSuggestions || $[28] !== t || $[29] !== takenOutputKeys) {
    t11 = (editable || node.fields.length > 0) && <><Divider /><div className="flex flex-col items-center gap-2"><Card.Root><SectionHeading hint={t(_temp21)} title={t(_temp22)} /><div className="flex flex-col gap-1">{node.fields.length === 0 ? <Card.Empty>{t(_temp23)}</Card.Empty> : node.fields.map((field, index_0) => <FieldRow nodeId={node.id} canDown={index_0 < node.fields.length - 1} canUp={index_0 > 0} editable={editable} field={field} isHighlighted={highlightedId === fieldRowId(field.uid)} key={field.uid} keyError={namedFieldKeyError({
              id: field.uid,
              name: field.key
            }, node.fields.map(_temp24))} onField={(next, options) => {
              onNode(current_1 => ({
                ...current_1,
                fields: current_1.fields.map(item_0 => item_0.uid === field.uid ? next : item_0)
              }), options);
            }} onMove={direction_0 => move(index_0, direction_0)} onRemove={() => {
              onNode(current_2 => ({
                ...current_2,
                fields: current_2.fields.filter(item_1 => item_1.uid !== field.uid)
              }), SAVE);
            }}>{renderFieldSuggestions?.(field.uid)}</FieldRow>)}</div></Card.Root>{editable && <Button onClick={() => {
          onNode(current_3 => ({
            ...current_3,
            fields: [...current_3.fields, newField(current_3, takenOutputKeys)]
          }), SAVE);
        }} prefixIcon={IcoPlusSmall16} size="compact" variant="secondary">{t(_temp25)}</Button>}</div></>;
    $[21] = editable;
    $[22] = highlightedId;
    $[23] = move;
    $[24] = node.fields;
    $[25] = node.id;
    $[26] = onNode;
    $[27] = renderFieldSuggestions;
    $[28] = t;
    $[29] = takenOutputKeys;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== editable || $[32] !== highlightedId || $[33] !== node.artifacts || $[34] !== onAddReport || $[35] !== onNode || $[36] !== onRemoveReport || $[37] !== renderReportSuggestions || $[38] !== t) {
    t12 = (editable || node.artifacts.length > 0) && <><Divider /><div className="flex flex-col items-center gap-2"><Card.Root><SectionHeading hint={t(_temp26)} title={t(_temp27)} /><div className="flex flex-col gap-1">{node.artifacts.length === 0 ? <Card.Empty>{t(_temp28)}</Card.Empty> : node.artifacts.map(artifact => <ReportCard key={artifact.uid} artifact={artifact} editable={editable} isHighlighted={highlightedId === artifactRowId(artifact.uid)} onArtifact={(next_0, options_0) => {
              onNode(current_4 => ({
                ...current_4,
                artifacts: current_4.artifacts.map(item_2 => item_2.uid === artifact.uid ? next_0 : item_2)
              }), options_0);
            }} onRemove={() => {
              onRemoveReport?.(artifact.uid);
              onNode(current_5 => ({
                ...current_5,
                artifacts: current_5.artifacts.filter(item_3 => item_3.uid !== artifact.uid)
              }), SAVE);
            }}>{renderReportSuggestions?.(artifact.uid)}</ReportCard>)}</div></Card.Root>{editable && onAddReport && node.artifacts.length === 0 && <Button onClick={onAddReport} prefixIcon={IcoPlusSmall16} size="compact" variant="secondary">{t(_temp29)}</Button>}</div></>;
    $[31] = editable;
    $[32] = highlightedId;
    $[33] = node.artifacts;
    $[34] = onAddReport;
    $[35] = onNode;
    $[36] = onRemoveReport;
    $[37] = renderReportSuggestions;
    $[38] = t;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t9) {
    t13 = <div className="flex flex-col gap-6 pt-1 pb-5">{t9}{t10}{t11}{t12}</div>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t9;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t13 || $[46] !== t3) {
    t14 = <TaskBlockProvider value={t3}>{t13}</TaskBlockProvider>;
    $[45] = t13;
    $[46] = t3;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  return t14;
};
export default TaskEditor;
function _temp($) {
  return $.unnamed;
}
function _temp2($_0) {
  return $_0.delete.title;
}
function _temp3($_1) {
  return $_1.delete.description;
}
function _temp4($_2) {
  return $_2.delete.confirm;
}
function _temp5($_3) {
  return $_3.delete.cancel;
}
function _temp6($_4) {
  return $_4.fallbackName;
}
function _temp7($_5) {
  return $_5.fallbackName;
}
function _temp8($_6) {
  return $_6.keyLabel;
}
function _temp9($_7) {
  return $_7.keyPlaceholder;
}
function _temp10($_8) {
  return $_8.typeLabel;
}
function _temp11($_9) {
  return $_9.remove;
}
function _temp12($_10) {
  return $_10.meaning;
}
function _temp13($_11) {
  return $_11.placeholder;
}
function _temp14($_12) {
  return $_12.columns.values;
}
function _temp15($_13) {
  return $_13.columns.criteria;
}
function _temp16($_14) {
  return $_14.columns.description;
}
function _temp17($_15) {
  return $_15.columns.key;
}
function _temp18($_16) {
  return $_16.columns.type;
}
function _temp19($) {
  return $.prompt.label;
}
function _temp20($_0) {
  return $_0.prompt.placeholder;
}
function _temp21($_1) {
  return $_1.outputs.hint;
}
function _temp22($_2) {
  return $_2.outputs.title;
}
function _temp23($_3) {
  return $_3.outputs.empty;
}
function _temp24(item) {
  return {
    id: item.uid,
    name: item.key
  };
}
function _temp25($_4) {
  return $_4.outputs.add;
}
function _temp26($_5) {
  return $_5.reports.hint;
}
function _temp27($_6) {
  return $_6.reports.title;
}
function _temp28($_7) {
  return $_7.reports.empty;
}
function _temp29($_8) {
  return $_8.reports.add;
}